import { useRef } from "react";
import { motion, useCycle, Variants } from "framer-motion";
import { useDimensions } from "@/hooks/use-dimensions";

const Path = (props: any) => (
  <motion.path
    fill="transparent"
    strokeWidth="3"
    stroke="current"
    strokeLinecap="round"
    {...props}
  />
);

const sidebar = {
  open: (height = 1000) => ({
    clipPath: `circle(${height * 2 + 200}px at 40px 40px)`,
    transition: {
      type: "spring",
      stiffness: 20,
      restDelta: 2,
    },
  }),
  closed: {
    clipPath: "circle(25px at 40px 40px)",
    transition: {
      delay: 0.5,
      type: "spring",
      stiffness: 400,
      damping: 40,
    },
  },
};

const variants: Variants = {
  open: {
    transition: {
      staggerChildren: 0.07,
      delayChildren: 0.2,
    },
    visibility: "visible",
  },
  closed: {
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1,
    },
    visibility: "hidden",
  },
};

type Props = {
  menuItems: { label: string; href: string }[];
  activePage: string;
};

export default ({ menuItems, activePage }: Props) => {
  const containerRef = useRef(null);
  const { height } = useDimensions(containerRef);
  const [isOpen, toggleOpen] = useCycle(false, true);
  const toggle = () => {
    if (isOpen) {
      document.body.style.overflow = "unset";
    } else {
      document.body.style.overflow = "hidden";
    }
    toggleOpen();
  };
  return (
    <motion.nav
      initial={false}
      animate={isOpen ? "open" : "closed"}
      custom={height}
      ref={containerRef}
      className="md:hidden"
    >
      <motion.div
        className="absolute top-0 left-0 w-full h-screen bg-secondary"
        variants={sidebar}
      />
      <motion.ul
        data-nosnippet
        className="absolute top-40 left-10 text-xl uppercase font-semibold"
        variants={variants}
      >
        {menuItems.map((i) => (
          <motion.li
            key={i.label}
            className="flex cursor-pointer text-primary"
            variants={{
              open: {
                y: 0,
                opacity: 1,
                transition: {
                  y: { stiffness: 1000, velocity: -100 },
                },
              },
              closed: {
                y: 50,
                opacity: 0,
                transition: {
                  y: { stiffness: 1000 },
                },
              },
            }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              className={i.href.endsWith(activePage) ? "font-bold" : ""}
              href={i.href}
              onClick={toggle}
            >
              {i.label}
            </a>
          </motion.li>
        ))}
      </motion.ul>
      <button
        title="menu"
        className="absolute cursor-pointer top-[18px] left-[15px] size-[50px] rounded-[50%] flex items-center justify-center stroke-primary"
        onClick={toggle}
      >
        <svg width="23" height="23" viewBox="0 0 23 23">
          <Path
            variants={{
              closed: { d: "M 2 2.5 L 20 2.5" },
              open: { d: "M 3 16.5 L 17 2.5" },
            }}
          />
          <Path
            d="M 2 9.423 L 20 9.423"
            variants={{
              closed: { opacity: 1 },
              open: { opacity: 0 },
            }}
            transition={{ duration: 0.1 }}
          />
          <Path
            variants={{
              closed: { d: "M 2 16.346 L 20 16.346" },
              open: { d: "M 3 2.5 L 17 16.346" },
            }}
          />
        </svg>
      </button>
    </motion.nav>
  );
};
